import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiUrl } from '../utils/api';
import {
  ArrowUpRight,
  ArrowDownLeft,
  RefreshCw,
  AlertTriangle,
  MessageSquare,
  Video,
  Star,
  Clock,
  CheckCircle,
  XCircle,
  ChevronDown,
  ExternalLink,
} from 'lucide-react';

const API_URL = getApiUrl();

const EVENT_CONFIG = {
  trial_video_watched: { label: 'Trial Video Watched', icon: Video, color: 'text-blue-600 bg-blue-50 border-blue-200' },
  feedback_submitted: { label: 'Feedback Submitted', icon: Star, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  class_completed: { label: 'Class Completed', icon: CheckCircle, color: 'text-green-600 bg-green-50 border-green-200' },
  membership_expiry_approaching: { label: 'Expiry Approaching', icon: Clock, color: 'text-orange-600 bg-orange-50 border-orange-200' },
  emergency_flagged: { label: 'Emergency Flagged', icon: AlertTriangle, color: 'text-red-600 bg-red-50 border-red-200' },
};

const PRIORITY_BADGE = {
  critical: 'bg-red-100 text-red-700 border border-red-200',
  high: 'bg-orange-100 text-orange-700 border border-orange-200',
  medium: 'bg-yellow-100 text-yellow-700 border border-yellow-200',
  low: 'bg-slate-100 text-slate-600 border border-slate-200',
};

const StatsCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid={`lms-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</span>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
    <p className="text-2xl font-bold text-slate-900">{value}</p>
  </div>
);

const EventRow = ({ event }) => {
  const [expanded, setExpanded] = useState(false);
  const config = EVENT_CONFIG[event.event_type] || { label: event.event_type, icon: MessageSquare, color: 'text-slate-600 bg-slate-50 border-slate-200' };
  const Icon = config.icon;

  return (
    <div className="border-b border-slate-100 last:border-0" data-testid={`lms-event-${event.event_id}`}>
      <div
        className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 border ${config.color}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-900">{config.label}</span>
            {event.priority && (
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full uppercase ${PRIORITY_BADGE[event.priority] || PRIORITY_BADGE.low}`}>
                {event.priority}
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 truncate">
            {event.payload?.user_name || event.payload?.instructor_name || event.payload?.batch_name || event.event_id}
            {event.lead_name && ` — ${event.lead_name}`}
          </p>
        </div>
        <span className="text-xs text-slate-400 flex-shrink-0">
          {new Date(event.received_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </div>
      {expanded && (
        <div className="px-3 pb-3 ml-11">
          <pre className="text-xs bg-slate-50 rounded-md p-3 overflow-x-auto border border-slate-200 text-slate-700">
            {JSON.stringify(event.payload, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

const SyncLogRow = ({ log }) => {
  const [expanded, setExpanded] = useState(false);
  const isSuccess = log.status === 'success';

  return (
    <div className="border-b border-slate-100 last:border-0" data-testid={`lms-sync-${log.lead_id}`}>
      <div
        className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isSuccess ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
          {isSuccess ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900 truncate">
            {log.request_payload?.user_email || log.lead_id}
          </p>
          <p className="text-xs text-slate-500">
            {log.request_payload?.membership_type} — {log.request_payload?.payment_currency} {log.request_payload?.payment_amount}
          </p>
        </div>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isSuccess ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {log.status}
        </span>
        <span className="text-xs text-slate-400 flex-shrink-0">
          {new Date(log.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </div>
      {expanded && (
        <div className="px-3 pb-3 ml-11 space-y-2">
          <div className="text-xs">
            <span className="font-medium text-slate-600">Response:</span>
            <pre className="bg-slate-50 rounded-md p-2 mt-1 overflow-x-auto border border-slate-200 text-slate-700">
              {JSON.stringify(log.response_body || log.error, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default function LmsIntegrationPage() {
  const [activeTab, setActiveTab] = useState('events');
  const [events, setEvents] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [eventFilter, setEventFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [syncStatusFilter, setSyncStatusFilter] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeTab === 'events') {
        if (eventFilter) params.event_type = eventFilter;
        if (priorityFilter) params.priority = priorityFilter;
        const [eventsRes, statsRes] = await Promise.all([
          axios.get(`${API_URL}/api/lms/events`, { params, withCredentials: true }),
          axios.get(`${API_URL}/api/lms/stats`, { withCredentials: true }),
        ]);
        setEvents(eventsRes.data.events || []);
        setStats(statsRes.data);
      } else {
        const syncParams = {};
        if (syncStatusFilter) syncParams.status = syncStatusFilter;
        const [logsRes, statsRes] = await Promise.all([
          axios.get(`${API_URL}/api/lms/sync-logs`, { params: syncParams, withCredentials: true }),
          axios.get(`${API_URL}/api/lms/stats`, { withCredentials: true }),
        ]);
        setSyncLogs(logsRes.data.logs || []);
        setStats(statsRes.data);
      }
    } catch (err) {
      console.error('LMS data fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, eventFilter, priorityFilter, syncStatusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const webhookUrl = `${window.location.origin}/api/lms/webhook/events`;

  return (
    <div className="p-4 lg:p-6 space-y-6" data-testid="lms-integration-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">LMS Integration</h1>
          <p className="text-sm text-slate-500 mt-0.5">OPS Panel ↔ Academy sync status</p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          data-testid="lms-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Webhook URL info */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-center gap-3 text-sm" data-testid="lms-webhook-url">
        <span className="text-slate-500 font-medium flex-shrink-0">Webhook URL for LMS:</span>
        <code className="bg-white px-2 py-1 rounded border border-slate-200 text-slate-700 text-xs font-mono truncate flex-1">
          {webhookUrl}
        </code>
        <button
          onClick={() => navigator.clipboard.writeText(webhookUrl)}
          className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex-shrink-0"
        >
          Copy
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="lms-stats-grid">
          <StatsCard label="Syncs Sent" value={stats.sync?.total || 0} icon={ArrowUpRight} color="bg-indigo-50 text-indigo-600" />
          <StatsCard label="Sync OK" value={stats.sync?.success || 0} icon={CheckCircle} color="bg-green-50 text-green-600" />
          <StatsCard label="Sync Errors" value={stats.sync?.errors || 0} icon={XCircle} color="bg-red-50 text-red-600" />
          <StatsCard label="Events Received" value={stats.events?.total || 0} icon={ArrowDownLeft} color="bg-blue-50 text-blue-600" />
          <StatsCard label="Critical/High" value={(stats.events?.critical || 0) + (stats.events?.high || 0)} icon={AlertTriangle} color="bg-orange-50 text-orange-600" />
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-slate-200">
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => setActiveTab('events')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'events' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid="lms-tab-events"
          >
            <ArrowDownLeft className="w-4 h-4 inline mr-1.5" />
            Inbound Events
          </button>
          <button
            onClick={() => setActiveTab('sync')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'sync' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid="lms-tab-sync"
          >
            <ArrowUpRight className="w-4 h-4 inline mr-1.5" />
            Outbound Syncs
          </button>
        </div>

        {/* Filters */}
        <div className="p-3 border-b border-slate-100 flex flex-wrap gap-2">
          {activeTab === 'events' ? (
            <>
              <select
                value={eventFilter}
                onChange={(e) => setEventFilter(e.target.value)}
                className="text-xs border border-slate-200 rounded-md px-2 py-1.5 text-slate-700 bg-white"
                data-testid="lms-filter-event-type"
              >
                <option value="">All Events</option>
                {Object.entries(EVENT_CONFIG).map(([key, cfg]) => (
                  <option key={key} value={key}>{cfg.label}</option>
                ))}
              </select>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="text-xs border border-slate-200 rounded-md px-2 py-1.5 text-slate-700 bg-white"
                data-testid="lms-filter-priority"
              >
                <option value="">All Priorities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </>
          ) : (
            <select
              value={syncStatusFilter}
              onChange={(e) => setSyncStatusFilter(e.target.value)}
              className="text-xs border border-slate-200 rounded-md px-2 py-1.5 text-slate-700 bg-white"
              data-testid="lms-filter-sync-status"
            >
              <option value="">All Statuses</option>
              <option value="success">Success</option>
              <option value="error">Error</option>
            </select>
          )}
        </div>

        {/* Content */}
        <div className="max-h-[600px] overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-slate-400 text-sm">Loading...</div>
          ) : activeTab === 'events' ? (
            events.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-sm" data-testid="lms-no-events">
                No LMS events received yet. Configure the LMS to send webhooks to the URL above.
              </div>
            ) : (
              events.map((event) => <EventRow key={event.event_id} event={event} />)
            )
          ) : (
            syncLogs.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-sm" data-testid="lms-no-syncs">
                No enrollment syncs recorded yet. Syncs are triggered automatically when payments are captured.
              </div>
            ) : (
              syncLogs.map((log, i) => <SyncLogRow key={log.lead_id + i} log={log} />)
            )
          )}
        </div>
      </div>

      {/* Integration guide */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-3">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          <ExternalLink className="w-4 h-4" />
          Integration Guide
        </h3>
        <div className="grid md:grid-cols-2 gap-4 text-xs text-slate-600">
          <div>
            <p className="font-medium text-slate-800 mb-1">OPS → LMS (Outbound)</p>
            <p>Enrollment data is automatically pushed to the LMS when any payment is captured (Razorpay or manual reconciliation). Check the "Outbound Syncs" tab for status.</p>
          </div>
          <div>
            <p className="font-medium text-slate-800 mb-1">LMS → OPS (Inbound)</p>
            <p>Configure the LMS to send event webhooks to the URL shown above. Events like trial video watches, feedback, and emergencies will appear in the "Inbound Events" tab.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
